import React, { ChangeEvent, Fragment, ReactElement, useEffect, useMemo, useState } from 'react';

import {
    Box,
    IconButton,
    Typography,
    Backdrop,
    CircularProgress,
    Button,
    Grid2,
    TextField,
    FormControl,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
    SelectChangeEvent,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import GoodsTable from '../goods/GoodsTable';
import { WarehouseClient } from '../../api/Extensions';
import { GoodsStatus, SiteDto, SiteType } from '../../api/ClientApi';
import SiteInfoExtended from './SiteInfoExtended';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import BulkInstallGoodsForm from '../goods/GoodsStatusManipulationForm/BulkInstallGoodsForm';
import SiteForm from './Form/SiteForm/SiteForm';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { RootState } from '../../redux/reducers';
import { importAllGoods } from '../../redux/actions/goodsActions';
import { DetailsContainerBox, DetailsInnerContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import { importApprovedOrderGoodsItems } from '../../redux/actions/orderGoodsItemsActions';
import { GoodsStatusSR } from '../../common/Types';
import { TableDetailsType, TableDetailsTypeSR } from '../goods/GoodsHelper';
import {
    canInstallGoods,
    canManipulateSites,
    isEmployeeFinanceManager,
    isEmployeeProjectManager,
    isEmployeeWarehouseManager,
    isEmployeeWarehouseOwnerOnly,
} from '../../redux/selectors/employeeSelector';
import SituationForm from './Form/SituationForm';
import { importClients } from '../../redux/actions/clientActions';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import SiteOwnerForm from './Form/SiteOwnerForm';
import SiteOwnersPopover from './SiteOwnersPopover';

export default function SiteDetails(): ReactElement {
    const isHighestPrivilageWarehouseOwner = useAppSelector(isEmployeeWarehouseOwnerOnly);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);
    const isFinanceManager = useAppSelector(isEmployeeFinanceManager);
    const isWarehouseManager = useAppSelector(isEmployeeWarehouseManager);
    const canChangeSites = useAppSelector(canManipulateSites);
    const canInstall = useAppSelector(canInstallGoods);

    const clients = useAppSelector((state) => state.clients.clients);

    const [site, setSite] = useState<SiteDto | undefined>(undefined);
    const [openUpdateSiteForm, setOpenUpdateSiteForm] = useState<boolean>(false);
    const [openCreateSituationForm, setOpenCreateSituationForm] = useState<boolean>(false);
    const [openBulkInstallForm, setOpenBulkUseForm] = useState<boolean>(false);

    const [tableDetailsType, setTableDetailsType] = useState<TableDetailsType | null>(TableDetailsType.Basic);

    const [articleFilter, setArticleFilter] = useState<string>('');
    const [labelsFilter, setLabelsFilter] = useState<string>('');
    const [uniqueBatchIdFilter, setUniqueBatchIdFilter] = useState<string>('');
    const [batchSuppliersFilter, setBatchSupplierFilter] = useState<string>('');
    const [arrivalStartTimeFilter, setArrivalStartTimeFilter] = useState<Date | undefined>(undefined);
    const [arrivalEndTimeFilter, setArrivalEndTimeFilter] = useState<Date | undefined>(undefined);
    const [goodsStatusFilter, setGoodsStatusFilter] = useState<GoodsStatus>(GoodsStatus.InStock);
    const [situationFilter, setSituationFilter] = useState<number | undefined>(undefined);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);

    const handleClickAnchorEl = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseAnchorEl = () => {
        setAnchorEl(null);
    };

    const sites = useAppSelector((state: RootState) => state.sites.sites);

    const goods = useAppSelector((state: RootState) => state.goods.goods);

    const approvedOrderGoodsItems = useAppSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);

    const currGoods = useMemo(() => {
        return goods?.filter((g) => g.siteId === site?.id);
    }, [goods, site]);

    const totalValueOnSite = useMemo(() => {
        return currGoods
            ?.filter((good) => good.goodsStatus === GoodsStatus.InStock)
            ?.reduce((acc, value) => {
                return (acc += (value.quantity ?? 0) * (value.batch?.purchasePricePerArticle ?? 0));
            }, 0);
    }, [currGoods]);

    const totalValueUsed = useMemo(() => {
        return currGoods
            ?.filter((good) => good.goodsStatus === GoodsStatus.Used)
            ?.reduce((acc, value) => {
                return (acc += (value.quantity ?? 0) * (value.batch?.purchasePricePerArticle ?? 0));
            }, 0);
    }, [currGoods]);

    const totalValueWrittenOff = useMemo(() => {
        return currGoods
            ?.filter((good) => good.goodsStatus === GoodsStatus.WrittenOff)
            ?.reduce((acc, value) => {
                return (acc += (value.quantity ?? 0) * (value.batch?.purchasePricePerArticle ?? 0));
            }, 0);
    }, [currGoods]);

    const totalValueSold = useMemo(() => {
        return currGoods
            ?.filter((good) => good.goodsStatus === GoodsStatus.Sold)
            ?.reduce((acc, value) => {
                return (acc += (value.quantity ?? 0) * (value.exitInfo?.sellingPrice ?? 0));
            }, 0);
    }, [currGoods]);

    const isSiteWithClient = useMemo(() => {
        if (!site) {
            return false;
        }
        if (!site.projectId || site.projectId <= 0) {
            return false;
        }
        return true;
    }, [site]);

    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const params = useParams();

    useEffect(() => {
        const siteId = params.siteId;
        if (!!siteId && !isNaN(+siteId) && +siteId > 0) {
            WarehouseClient.getSiteById(+siteId, true, true).then((res) => {
                setSite(res);
            });
        }
    }, [params, sites]);

    useEffect(() => {
        if (!goods) {
            dispatch(importAllGoods());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!clients) {
            dispatch(importClients());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!approvedOrderGoodsItems) {
            dispatch(importApprovedOrderGoodsItems());
        }
    }, [dispatch]);

    const currOrderGoodsItems = useMemo(() => {
        return approvedOrderGoodsItems?.filter((ogi) => ogi.order?.originSiteId === site?.id);
    }, [approvedOrderGoodsItems, site]);

    const handleArticleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setArticleFilter(value);
    };

    const handleLabelFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLabelsFilter(value);
    };

    const handleUniqueBatchIdFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setUniqueBatchIdFilter(value);
    };

    const handleBatchSupplierFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setBatchSupplierFilter(value);
    };

    const handleGoodsStatusFilterChange = (event: SelectChangeEvent<GoodsStatus>) => {
        const value = event.target.value;

        switch (value) {
            case GoodsStatus[GoodsStatus.Sold]:
                setGoodsStatusFilter(GoodsStatus.Sold);
                break;
            case GoodsStatus[GoodsStatus.Used]:
                setGoodsStatusFilter(GoodsStatus.Used);
                break;
            case GoodsStatus[GoodsStatus.WrittenOff]:
                setGoodsStatusFilter(GoodsStatus.WrittenOff);
                break;
            default:
                setGoodsStatusFilter(GoodsStatus.InStock);
        }
    };

    const handleSituationFilterChange = (id: number) => {
        if (situationFilter === id) {
            setSituationFilter(undefined);
        } else {
            setSituationFilter(id);
        }
    };

    const clearAllFilters = () => {
        setArticleFilter('');
        setLabelsFilter('');
        setUniqueBatchIdFilter('');
        setBatchSupplierFilter('');
        setGoodsStatusFilter(GoodsStatus.InStock);
        setArrivalStartTimeFilter(undefined);
        setArrivalEndTimeFilter(undefined);
        setSituationFilter(undefined);
    };

    const handleOpenSiteForm = () => {
        setOpenUpdateSiteForm(true);
    };

    const handleCloseSiteForm = () => {
        setOpenUpdateSiteForm(false);
    };

    const handleOpenSituationForm = () => {
        setOpenCreateSituationForm(true);
    };

    const handleCloseSituationForm = () => {
        setOpenCreateSituationForm(false);
    };

    const handleOpenBulkInstallForm = () => {
        setOpenBulkUseForm(true);
    };

    const handleCloseBulkInstallForm = () => {
        setOpenBulkUseForm(false);
    };

    const handleClose = () => {
        navigate('..');
    };

    const goToForm = () => {
        navigate('goodsToSite');
    };

    const [openOwnerForm, setOpenOwnerForm] = useState<boolean>(false);

    const handleOpenOwnerForm = () => {
        setOpenOwnerForm(true);
    };

    const handleCloseOwnerForm = () => {
        setOpenOwnerForm(false);
    };

    const handleChangeTableDetailsType = (event: React.MouseEvent<HTMLElement, MouseEvent>, value: any) => {
        setTableDetailsType(value);
    };

    const isBasicView = tableDetailsType === TableDetailsType.Basic || !tableDetailsType;
    const isExitIfoView = tableDetailsType === TableDetailsType.Exit;

    return (
        <DetailsContainerBox
            sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
            }}
        >
            <Grid2
                container
                sx={{
                    py: 2,
                    px: 4,
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Grid2>
                    <Box sx={{ display: 'flex' }}>
                        <IconButton color="inherit" onClick={handleClose} sx={{ p: 0 }}>
                            <ArrowBackIosIcon />
                        </IconButton>
                        <Typography variant="h5" component="h6">
                            Skladište - detalji
                        </Typography>
                    </Box>
                </Grid2>

                <Grid2 sx={{ display: 'flex', gap: 2 }}>
                    {!isHighestPrivilageWarehouseOwner && canChangeSites && (
                        <Grid2 container sx={{ gap: 1 }}>
                            {!!site && (
                                <Grid2>
                                    <Button
                                        color="warning"
                                        variant="contained"
                                        onClick={handleOpenSiteForm}
                                        startIcon={<EditIcon />}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Izmeni skladište
                                    </Button>
                                </Grid2>
                            )}
                            <Grid2>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={goToForm}
                                    startIcon={<AddIcon />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Dodaj robu
                                </Button>
                            </Grid2>
                        </Grid2>
                    )}
                    {canInstall && site?.siteType === SiteType.ConstructionSite && isSiteWithClient && (
                        <Grid2>
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={handleOpenBulkInstallForm}
                                startIcon={<DownloadIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Ugradi više
                            </Button>
                        </Grid2>
                    )}
                    <SiteOwnersPopover
                        handleOpenOwnerForm={handleOpenOwnerForm}
                        warehouseOwners={site?.warehouseOwnersAzObjIds ?? []}
                        projectManagers={site?.project?.projectManagersAzObjIds ?? []}
                    />
                </Grid2>
            </Grid2>
            <Box sx={{ '& > :not(style)': { m: 1 } }}>
                <DetailsInnerContainerBox>
                    {!site ? (
                        <Fragment>
                            <Backdrop
                                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                                open={!Boolean(site)}
                            >
                                <CircularProgress color="inherit" />
                            </Backdrop>
                        </Fragment>
                    ) : (
                        <Box>
                            <SiteInfoExtended
                                site={site}
                                siteTotalValue={totalValueOnSite}
                                totalValueUsed={totalValueUsed}
                                totalValueWrittenOff={totalValueWrittenOff}
                                totalValueSold={totalValueSold}
                            />

                            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                {!isHighestPrivilageWarehouseOwner &&
                                    canChangeSites &&
                                    site?.siteType === SiteType.ConstructionSite && (
                                        <IconButton color="primary" onClick={handleOpenSituationForm}>
                                            <AddIcon />
                                        </IconButton>
                                    )}
                                <Typography variant="body1">Situacije:</Typography>
                                {site?.situations?.map((situation) => {
                                    return situation.title ? (
                                        isExitIfoView ? (
                                            <div
                                                key={situation.title}
                                                onClick={() => handleSituationFilterChange(situation.id ?? 0)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Chip
                                                    color="primary"
                                                    variant={situationFilter === situation.id ? 'filled' : 'outlined'}
                                                    label={situation.title ?? ''}
                                                />
                                            </div>
                                        ) : (
                                            <Chip
                                                key={situation.title}
                                                color="default"
                                                variant="outlined"
                                                label={situation.title ?? ''}
                                            />
                                        )
                                    ) : (
                                        <></>
                                    );
                                })}
                            </Box>
                            <Grid2 container sx={{ display: 'flex', gap: 2, flexDirection: 'start' }}>
                                <TextField
                                    size="small"
                                    sx={{ mb: 1, width: '200px' }}
                                    placeholder="Artikal"
                                    value={articleFilter}
                                    onChange={handleArticleFilterChange}
                                />
                                <TextField
                                    size="small"
                                    sx={{ mb: 1, width: '200px' }}
                                    placeholder="Tag"
                                    value={labelsFilter}
                                    onChange={handleLabelFilterChange}
                                />
                                {!isBasicView && !isExitIfoView && isProjectManager && (
                                    <TextField
                                        size="small"
                                        sx={{ mb: 1, width: '200px' }}
                                        placeholder="Br.fakture"
                                        value={uniqueBatchIdFilter}
                                        onChange={handleUniqueBatchIdFilterChange}
                                    />
                                )}
                                {!isBasicView && !isExitIfoView && isProjectManager && (
                                    <TextField
                                        size="small"
                                        sx={{ mb: 1, width: '200px' }}
                                        placeholder="Dobavljač"
                                        value={batchSuppliersFilter}
                                        onChange={handleBatchSupplierFilterChange}
                                    />
                                )}
                                {!isBasicView && (
                                    <FormControl size="small" sx={{ mb: 1, width: '200px' }}>
                                        <InputLabel id="demo-simple-select-label">Status robe</InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            value={goodsStatusFilter ?? ''}
                                            label="Status robe"
                                            onChange={handleGoodsStatusFilterChange}
                                        >
                                            {Object.values(GoodsStatus).map((gs) => {
                                                if (gs !== GoodsStatus.InTransport && gs !== GoodsStatus.Ordered) {
                                                    return (
                                                        <MenuItem key={gs} value={gs as GoodsStatus}>
                                                            {GoodsStatusSR[gs]}
                                                        </MenuItem>
                                                    );
                                                }
                                            })}
                                        </Select>
                                    </FormControl>
                                )}
                                <Box sx={{ flexGrow: 1 }} />
                                <Box>
                                    <Button onClick={clearAllFilters}>Očisti filtere</Button>
                                </Box>
                            </Grid2>
                            <Box sx={{ p: 0, mt: 2 }} />
                            <Box display="flex" justifyContent="end">
                                <ToggleButtonGroup
                                    color="primary"
                                    value={tableDetailsType}
                                    exclusive
                                    onChange={handleChangeTableDetailsType}
                                >
                                    <Tooltip title={TableDetailsTypeSR[TableDetailsType.Basic]}>
                                        <ToggleButton value={TableDetailsType.Basic}>
                                            <DensityLargeIcon />
                                        </ToggleButton>
                                    </Tooltip>
                                    <Tooltip title={TableDetailsTypeSR[TableDetailsType.Exit]}>
                                        <ToggleButton value={TableDetailsType.Exit}>
                                            <DensityMediumIcon />
                                        </ToggleButton>
                                    </Tooltip>
                                    {(isProjectManager || isFinanceManager || isWarehouseManager) && (
                                        <Tooltip title={TableDetailsTypeSR[TableDetailsType.Arrival]}>
                                            <ToggleButton value={TableDetailsType.Arrival}>
                                                <DensitySmallIcon />
                                            </ToggleButton>
                                        </Tooltip>
                                    )}
                                </ToggleButtonGroup>
                            </Box>
                            <Box sx={{ p: 0, mt: 2 }} />
                            <DetailsTableContainerBox>
                                <GoodsTable
                                    isSiteTable
                                    tableDetailsType={tableDetailsType}
                                    isSiteWithClient={isSiteWithClient}
                                    goods={currGoods ?? null}
                                    approvedOrderGoodsItems={currOrderGoodsItems ?? null}
                                    articleFilter={articleFilter.trim()}
                                    labelsFilter={labelsFilter.trim()}
                                    goodsStatusFilter={goodsStatusFilter}
                                    situationFilter={situationFilter}
                                    batchSuppliersFilter={batchSuppliersFilter.trim()}
                                    siteTypeFilter={null}
                                    siteNameFilter={''}
                                    uniqueBatchIdFilter={uniqueBatchIdFilter.trim()}
                                />
                            </DetailsTableContainerBox>
                        </Box>
                    )}
                </DetailsInnerContainerBox>
            </Box>
            {!isHighestPrivilageWarehouseOwner && (
                <SiteForm open={openUpdateSiteForm} closeForm={handleCloseSiteForm} />
            )}
            {site?.siteType === SiteType.ConstructionSite && (
                <SituationForm open={openCreateSituationForm} closeForm={handleCloseSituationForm} />
            )}
            {canInstall && site?.siteType === SiteType.ConstructionSite && isSiteWithClient && (
                <BulkInstallGoodsForm open={openBulkInstallForm} closeForm={handleCloseBulkInstallForm} />
            )}
            {(isWarehouseManager || isProjectManager) && (
                <SiteOwnerForm closeForm={handleCloseOwnerForm} open={openOwnerForm} site={site} />
            )}
        </DetailsContainerBox>
    );
}
